import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/authContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Users, UserPlus, CalendarDays, Layers, DollarSign, TrendingUp, Wallet } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const ADMIN_EMAIL = "enricarloni@gmail.com";

// Oplif brand palette (intentionally hardcoded — admin-only page, brand-locked).
const BRAND = {
  bg: "#ffffff",
  card: "#ffffff",
  border: "#e6e6e6",
  green: "#2d8653",
  orange: "#e76f00",
  dark: "#1a1a1a",
  text: "#1a1a1a",
  muted: "#6b6b6b",
  subtle: "#f7f7f5",
};

// Starter is deprecated and treated as Free everywhere — keep the key in the
// type only because the database enum still has it for legacy rows.
type PlanKey = "free" | "starter" | "pro" | "business" | "enterprise";

interface AdminUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  plan: PlanKey;
}

interface RevenueData {
  mrr: number;
  totalRevenue: number;
  payingPerPlan: Record<string, number>;
  mrrPerPlan: Record<string, number>;
  monthly: { label: string; revenue: number }[];
  planPrices: Record<string, number>;
}

interface AdminData {
  total: number;
  newToday: number;
  newThisWeek: number;
  planCounts: Record<PlanKey, number>;
  users: AdminUser[];
  revenue?: RevenueData;
}

const PLAN_LABELS: Record<PlanKey, string> = {
  free: "Free",
  // Legacy rows still on 'starter' display as Free since the tier is removed.
  starter: "Free (legacy)",
  pro: "Pro",
  business: "Studio",
  enterprise: "Enterprise",
};

const formatDate = (iso: string | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatUSD = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState<AdminData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = user?.email?.toLowerCase() === ADMIN_EMAIL;

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("admin-users");
        if (error) throw error;
        setData(data as AdminData);
      } catch (e: any) {
        setError(e?.message ?? "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: BRAND.bg }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: BRAND.muted }} />
      </div>
    );
  }

  if (!user) return <Navigate to="/" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;

  const statCards = data
    ? [
        { label: "Total users", value: data.total, icon: Users, accent: BRAND.green },
        { label: "New today", value: data.newToday, icon: UserPlus, accent: BRAND.orange },
        { label: "New this week", value: data.newThisWeek, icon: CalendarDays, accent: BRAND.green },
        {
          label: "Paid users",
          value:
            (data.planCounts.starter ?? 0) +
            data.planCounts.pro +
            data.planCounts.business +
            data.planCounts.enterprise,
          icon: Layers,
          accent: BRAND.orange,
        },
      ]
    : [];

  // Display order — starter is deprecated and shown only if any legacy rows remain.
  const planOrder: PlanKey[] = ["free", "pro", "business", "enterprise", "starter"];
  const paidPlans: PlanKey[] = ["pro", "business", "enterprise"];

  return (
    <div className="min-h-screen p-6 md:p-10" style={{ backgroundColor: BRAND.bg, color: BRAND.text }}>
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl md:text-4xl font-medium tracking-tight" style={{ color: BRAND.dark }}>
              Oplif Admin
            </h1>
            <p className="text-sm mt-1" style={{ color: BRAND.muted }}>
              Restricted access · {user.email}
            </p>
          </div>
          <div
            className="text-xs uppercase tracking-wider px-3 py-1 rounded-full font-medium"
            style={{ backgroundColor: `${BRAND.green}1a`, color: BRAND.green, border: `1px solid ${BRAND.green}55` }}
          >
            Admin
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: BRAND.muted }} />
          </div>
        ) : error ? (
          <Card style={{ backgroundColor: BRAND.card, borderColor: BRAND.border }}>
            <CardContent className="pt-6" style={{ color: BRAND.orange }}>
              {error}
            </CardContent>
          </Card>
        ) : data ? (
          <>
            {/* Top stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {statCards.map((s) => {
                const Icon = s.icon;
                return (
                  <Card
                    key={s.label}
                    style={{ backgroundColor: BRAND.card, borderColor: BRAND.border }}
                    className="border shadow-sm"
                  >
                    <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                      <CardTitle className="text-xs font-medium uppercase tracking-wider" style={{ color: BRAND.muted }}>
                        {s.label}
                      </CardTitle>
                      <Icon className="w-4 h-4" style={{ color: s.accent }} />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-medium" style={{ color: BRAND.dark }}>
                        {s.value}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Plan breakdown */}
            <Card style={{ backgroundColor: BRAND.card, borderColor: BRAND.border }} className="border shadow-sm">
              <CardHeader>
                <CardTitle style={{ color: BRAND.dark }}>Users by plan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {planOrder.map((p) => {
                    const isPaid = p !== "free";
                    return (
                      <div
                        key={p}
                        className="rounded-lg px-4 py-3 border"
                        style={{
                          backgroundColor: BRAND.subtle,
                          borderColor: isPaid ? `${BRAND.orange}55` : BRAND.border,
                        }}
                      >
                        <div
                          className="text-xs uppercase tracking-wider font-medium"
                          style={{ color: isPaid ? BRAND.orange : BRAND.muted }}
                        >
                          {PLAN_LABELS[p]}
                        </div>
                        <div className="text-2xl font-medium mt-1" style={{ color: BRAND.dark }}>
                          {data.planCounts[p] ?? 0}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* ====== REVENUE SECTION ====== */}
            {data.revenue && (
              <section
                className="rounded-xl border p-6 space-y-6"
                style={{ backgroundColor: BRAND.subtle, borderColor: BRAND.border }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: BRAND.green }}
                  >
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-medium" style={{ color: BRAND.dark }}>
                      Revenue
                    </h2>
                    <p className="text-sm" style={{ color: BRAND.muted }}>
                      MRR, total revenue, and growth — based on active subscriptions
                    </p>
                  </div>
                </div>

                {/* Top revenue KPIs */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border shadow-sm" style={{ backgroundColor: BRAND.card, borderColor: BRAND.border }}>
                    <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                      <CardTitle className="text-xs font-medium uppercase tracking-wider" style={{ color: BRAND.muted }}>
                        Monthly Recurring Revenue
                      </CardTitle>
                      <TrendingUp className="w-4 h-4" style={{ color: BRAND.green }} />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-medium" style={{ color: BRAND.green }}>
                        {formatUSD(data.revenue.mrr)}
                      </div>
                      <div className="text-xs mt-1" style={{ color: BRAND.muted }}>
                        per month
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border shadow-sm" style={{ backgroundColor: BRAND.card, borderColor: BRAND.border }}>
                    <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                      <CardTitle className="text-xs font-medium uppercase tracking-wider" style={{ color: BRAND.muted }}>
                        Total revenue to date
                      </CardTitle>
                      <Wallet className="w-4 h-4" style={{ color: BRAND.orange }} />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-medium" style={{ color: BRAND.dark }}>
                        {formatUSD(data.revenue.totalRevenue)}
                      </div>
                      <div className="text-xs mt-1" style={{ color: BRAND.muted }}>
                        cumulative
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border shadow-sm" style={{ backgroundColor: BRAND.card, borderColor: BRAND.border }}>
                    <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                      <CardTitle className="text-xs font-medium uppercase tracking-wider" style={{ color: BRAND.muted }}>
                        Annual run rate
                      </CardTitle>
                      <DollarSign className="w-4 h-4" style={{ color: BRAND.green }} />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-medium" style={{ color: BRAND.dark }}>
                        {formatUSD(data.revenue.mrr * 12)}
                      </div>
                      <div className="text-xs mt-1" style={{ color: BRAND.muted }}>
                        MRR × 12
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Revenue breakdown by plan */}
                <Card className="border shadow-sm" style={{ backgroundColor: BRAND.card, borderColor: BRAND.border }}>
                  <CardHeader>
                    <CardTitle style={{ color: BRAND.dark }}>Revenue by plan</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow style={{ borderColor: BRAND.border }}>
                            <TableHead style={{ color: BRAND.muted }}>Plan</TableHead>
                            <TableHead style={{ color: BRAND.muted }}>Price</TableHead>
                            <TableHead style={{ color: BRAND.muted }}>Paying users</TableHead>
                            <TableHead style={{ color: BRAND.muted }} className="text-right">
                              MRR contribution
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paidPlans.map((p) => {
                            const price = data.revenue!.planPrices[p] ?? 0;
                            const count = data.revenue!.payingPerPlan[p] ?? 0;
                            const mrrContrib = data.revenue!.mrrPerPlan[p] ?? 0;
                            return (
                              <TableRow key={p} style={{ borderColor: BRAND.border }}>
                                <TableCell className="font-medium" style={{ color: BRAND.dark }}>
                                  <span
                                    className="inline-block px-2 py-0.5 rounded text-xs font-medium uppercase tracking-wider"
                                    style={{
                                      backgroundColor: `${BRAND.orange}1a`,
                                      color: BRAND.orange,
                                      border: `1px solid ${BRAND.orange}55`,
                                    }}
                                  >
                                    {PLAN_LABELS[p]}
                                  </span>
                                </TableCell>
                                <TableCell style={{ color: BRAND.dark }}>
                                  {p === "enterprise" ? (
                                    <span style={{ color: BRAND.muted }}>Custom</span>
                                  ) : (
                                    `${formatUSD(price)}/mo`
                                  )}
                                </TableCell>
                                <TableCell style={{ color: BRAND.dark }}>{count}</TableCell>
                                <TableCell className="text-right font-medium" style={{ color: BRAND.green }}>
                                  {p === "enterprise" && mrrContrib === 0 ? "—" : formatUSD(mrrContrib)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow style={{ borderColor: BRAND.border, backgroundColor: BRAND.subtle }}>
                            <TableCell className="font-medium" style={{ color: BRAND.dark }}>
                              Total
                            </TableCell>
                            <TableCell />
                            <TableCell className="font-medium" style={{ color: BRAND.dark }}>
                              {paidPlans.reduce((acc, p) => acc + (data.revenue!.payingPerPlan[p] ?? 0), 0)}
                            </TableCell>
                            <TableCell className="text-right font-medium" style={{ color: BRAND.green }}>
                              {formatUSD(data.revenue.mrr)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>

                {/* Revenue chart */}
                <Card className="border shadow-sm" style={{ backgroundColor: BRAND.card, borderColor: BRAND.border }}>
                  <CardHeader>
                    <CardTitle style={{ color: BRAND.dark }}>Revenue growth (last 12 months)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="w-full h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data.revenue.monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={BRAND.green} stopOpacity={0.4} />
                              <stop offset="100%" stopColor={BRAND.green} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke={BRAND.border} />
                          <XAxis dataKey="label" stroke={BRAND.muted} fontSize={12} />
                          <YAxis stroke={BRAND.muted} fontSize={12} tickFormatter={(v) => `$${v}`} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: BRAND.dark,
                              border: `1px solid ${BRAND.border}`,
                              borderRadius: 8,
                              color: "#fff",
                            }}
                            formatter={(value: number) => [formatUSD(value), "MRR"]}
                          />
                          <Area
                            type="monotone"
                            dataKey="revenue"
                            stroke={BRAND.green}
                            strokeWidth={2}
                            fill="url(#revGradient)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </section>
            )}

            {/* Users table */}
            <Card style={{ backgroundColor: BRAND.card, borderColor: BRAND.border }} className="border shadow-sm">
              <CardHeader>
                <CardTitle style={{ color: BRAND.dark }}>All users ({data.users.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow style={{ borderColor: BRAND.border }}>
                        <TableHead style={{ color: BRAND.muted }}>Email</TableHead>
                        <TableHead style={{ color: BRAND.muted }}>Plan</TableHead>
                        <TableHead style={{ color: BRAND.muted }}>Signed up</TableHead>
                        <TableHead style={{ color: BRAND.muted }}>Last sign-in</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.users.map((u) => {
                        const isPaid = u.plan !== "free";
                        return (
                          <TableRow key={u.id} style={{ borderColor: BRAND.border }}>
                            <TableCell className="font-medium" style={{ color: BRAND.dark }}>
                              {u.email}
                            </TableCell>
                            <TableCell>
                              <span
                                className="inline-block px-2 py-0.5 rounded text-xs font-medium uppercase tracking-wider"
                                style={{
                                  backgroundColor: isPaid ? `${BRAND.orange}1a` : `${BRAND.green}1a`,
                                  color: isPaid ? BRAND.orange : BRAND.green,
                                  border: `1px solid ${isPaid ? BRAND.orange : BRAND.green}55`,
                                }}
                              >
                                {PLAN_LABELS[u.plan]}
                              </span>
                            </TableCell>
                            <TableCell style={{ color: BRAND.muted }}>{formatDate(u.created_at)}</TableCell>
                            <TableCell style={{ color: BRAND.muted }}>{formatDate(u.last_sign_in_at)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>
    </div>
  );
}
